/**
 * Data preview card. Renders the most recent `/preview`
 * result as a scrollable table. Hidden until the first Preview click; shows a
 * spinner while loading, an empty/warning message when no rows match, and the
 * error in danger styling on failure. All state comes from `preview-state.ts`.
 *
 * Lit escapes every cell, so no manual `escHtml` is needed (the blob built the
 * table as an HTML string and had to escape by hand).
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  previewVisible,
  previewLoading,
  previewData,
  previewError,
} from './preview-state.ts';

@customElement('data-preview')
export class DataPreview extends LightElement {
  private store = new StoreController(this, [
    previewVisible,
    previewLoading,
    previewData,
    previewError,
  ]);

  private renderBody(): TemplateResult {
    if (previewLoading.value) {
      return html`<div class="loading-overlay"><span class="spinner spinner-md" aria-hidden="true"></span> Loading preview…</div>`;
    }
    if (previewError.value) {
      return html`<div class="loading-overlay error">${previewError.value}</div>`;
    }
    const data = previewData.value;
    if (!data) return html`<div class="loading-overlay">No preview yet</div>`;
    if (data.rows.length === 0) {
      return html`<div class="loading-overlay">
        ${data.warning || 'No rows match the current filters'}
      </div>`;
    }
    return html`
      ${data.warning
        ? html`<div class="warning-box visible preview-warning">${data.warning}</div>`
        : nothing}
      <table class="preview-table">
        <thead>
          <tr>
            ${data.columns.map((c) => html`<th>${c}</th>`)}
          </tr>
        </thead>
        <tbody>
          ${data.rows.map(
            (row) => html`
              <tr>
                ${row.map((val) => {
                  const text = String(val ?? '');
                  return html`<td title=${text}>${text}</td>`;
                })}
              </tr>
            `,
          )}
        </tbody>
      </table>
    `;
  }

  override render(): TemplateResult | typeof nothing {
    if (!previewVisible.value) return nothing;
    const data = previewData.value;
    const count = !previewLoading.value && data ? data.rows.length : null;
    return html`
      <div class="card preview-card" data-tab="export">
        <h2>
          Preview
          ${count !== null ? html`<span class="preview-status">(${count} rows)</span>` : nothing}
        </h2>
        <div class="preview-table-wrap scroll-thin">${this.renderBody()}</div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'data-preview': DataPreview;
  }
}
